/* eslint-disable react/jsx-props-no-spreading */
import { FC } from 'react';
import { format } from 'date-fns/esm';
import { Link } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import NumberFormat from 'react-number-format';

import {
  currencyFormatDecimals,
  numberFormatDecimals,
} from 'constants/formats';
import { Draft } from 'types';
import draftTypes from 'constants/draftTypes';

import { useGetDraftsSectionPermissions } from 'modules/drafts/hooks/useGetDraftsSectionPermissions';

import { getDraftStatusLabel } from 'modules/drafts/utils/getDraftStatusLabel';
import MenuDots from '../../../_shared/components/MenuDots';
import ncMenuOptions from './ncMenuOptions';
import draftMenuOptions from './draftMenuOptions';

const draftOperations = {
  DRAFT_SELL: 'Venta',
  DRAFT_SAFE: 'S.A.F.E.',
  DRAFT_BUY_SELL_SHARES: 'Compra/Venta',
  DRAFT_CAPITAL_INCREASE: 'Ampliación',
  DRAFT_CONVERTIBLE_NOTE: 'Nota Convertible',
};

const draftRoutes = {
  DRAFT_SELL: 'borrador-venta',
  DRAFT_SAFE: 'borrador-safe',
  DRAFT_BUY_SELL_SHARES: 'borrador-compra-venta',
  DRAFT_CAPITAL_INCREASE: 'borrador-ampliacion',
  DRAFT_CONVERTIBLE_NOTE: 'borrador-nota',
};

type DraftsRowProps = {
  index: number;
  societyId: string;
  draft: Draft;
};

const DraftsRow: FC<DraftsRowProps> = ({ index, societyId, draft }) => {
  const { t } = useTranslate();
  const { isReadOnly } = useGetDraftsSectionPermissions().data;

  const {
    name,
    date,
    percent,
    valuation,
    totalContribution,
    draftType,
    status,
    isConverted,
  } = draft;

  return (
    <tr className="nk-tb-item">
      <td className="nk-tb-col tb-col-xl">
        <span>{index}</span>
      </td>

      <td className="nk-tb-col">
        <div className="user-card">
          <div className="user-avatar bg-dim-primary d-none d-sm-flex">
            <span>{name.slice(0, 1).toUpperCase()}</span>
          </div>
          <div className="user-info">
            <span className="tb-lead d-block d-md-none text-ellipsis w-100px">
              <Link
                to={`/${draftRoutes[draftType]}/${societyId}/${draft['_id']}`}
              >
                {name || ''}
              </Link>
            </span>
            <span className="tb-lead d-none d-md-block">
              <Link
                to={`/${draftRoutes[draftType]}/${societyId}/${draft['_id']}`}
              >
                {name || ''}
              </Link>
            </span>
          </div>
        </div>
      </td>

      <td className="nk-tb-col">
        <span className="d-block d-md-none">
          {format(new Date(date), 'dd/MM/yy')}
        </span>
        <span className="d-none d-md-block">
          {format(new Date(date), 'dd/MM/yyyy')}
        </span>
      </td>

      <td className="nk-tb-col tb-col-xl text-right">
        <span>
          <NumberFormat
            value={percent || 0}
            displayType="text"
            {...numberFormatDecimals}
          />
          %
        </span>
      </td>

      <td className="nk-tb-col tb-col-xl text-right">
        <span>
          <NumberFormat
            value={valuation || 0}
            displayType="text"
            {...currencyFormatDecimals}
          />
        </span>
      </td>

      <td className="nk-tb-col text-right">
        <span>
          <NumberFormat
            value={totalContribution || 0}
            displayType="text"
            {...currencyFormatDecimals}
          />
        </span>
      </td>

      <td className="nk-tb-col">
        <span className="badge badge-dot text-success">
          {draftOperations[draftType]}
        </span>
      </td>

      <td className="nk-tb-col tb-col-xl">
        <span>
          {getDraftStatusLabel({ draftType, status, isConverted, t })}
        </span>
      </td>

      <td className="nk-tb-col  nk-tb-col-tools">
        <ul className="nk-tb-actions gx-1">
          <li>
            <div className="dropdown">
              {draftType === draftTypes.DRAFT_CONVERTIBLE_NOTE ||
              draftType === draftTypes.DRAFT_SAFE ? (
                <MenuDots
                  menuOptions={ncMenuOptions}
                  // @ts-ignore
                  id={draft['_id']}
                  params={{
                    draftId: draft['_id'],
                    data: draft,
                    isReadOnly,
                  }}
                  direction="left"
                />
              ) : (
                <MenuDots
                  // @ts-ignore
                  id={draft['_id']}
                  menuOptions={draftMenuOptions}
                  params={{
                    draftId: draft['_id'],
                    data: draft,
                    isReadOnly,
                  }}
                  direction="left"
                />
              )}
            </div>
          </li>
        </ul>
      </td>
    </tr>
  );
};

export default DraftsRow;
